import { CommonModule } from '@angular/common';
import { Component, computed, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { BackgroundOperationState } from '../../state-stores/background-operation.state';

/**
 * Always-mounted (app shell level) stack of non-blocking corner toasts, one
 * per BackgroundOperationState-tracked pending operation. Never intrudes on
 * other UI clicks, and stays visible regardless of which page the user is on.
 * Toasts can be dismissed from the UI without affecting the underlying
 * operation, which keeps running/polling in the background regardless.
 */
@Component({
  selector: 'app-background-operation-pending-toast',
  standalone: true,
  imports: [CommonModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './background-operation-pending-toast.component.html',
})
export class BackgroundOperationPendingToastComponent {
  private readonly dismissedIds = signal<Set<string>>(new Set());

  readonly visibleOperations = computed(() => {
    const dismissed = this.dismissedIds();
    return this.backgroundOperationState.pendingOperations().filter(op => !dismissed.has(op.id));
  });

  constructor(
    protected readonly backgroundOperationState: BackgroundOperationState,
  ) {
    // Re-arm dismissal so a future run of the same operation id shows its toast again.
    effect(() => {
      const activeIds = new Set(this.backgroundOperationState.pendingOperations().map(op => op.id));
      const dismissed = this.dismissedIds();
      const pruned = new Set([...dismissed].filter(id => activeIds.has(id)));
      if (pruned.size !== dismissed.size) {
        this.dismissedIds.set(pruned);
      }
    }, { allowSignalWrites: true });
  }

  dismiss(id: string): void {
    const next = new Set(this.dismissedIds());
    next.add(id);
    this.dismissedIds.set(next);
  }
}

